"use client";

import { useEffect, useState } from "react";
import { Search, Mail, Calendar, Chrome, Users, FileText } from "lucide-react";

const features = [
  {
    title: "Deep Prospect Research",
    description: "Sabine researches your prospects across the web, LinkedIn, and news sources. Get a complete picture before every conversation.",
    icon: Search,
    metric: "AI-Powered",
  },
  {
    title: "Personalized Outreach",
    description: "Cold emails, LinkedIn messages, and call scripts generated from real prospect signals. Not templates — personalized execution.",
    icon: Mail,
    metric: "1-Click Content",
  },
  {
    title: "Calendar Auto-Prep",
    description: "Connect Google Calendar and Sabine automatically prepares research for upcoming meetings. Walk in informed without lifting a finger.",
    icon: Calendar,
    metric: "Auto-Generated",
  },
  {
    title: "Chrome Extension",
    description: "Research prospects directly from LinkedIn, company websites, or your CRM with a single click. Execution starts where your reps already work.",
    icon: Chrome,
    metric: "1-Click Research",
  },
  {
    title: "Contact Discovery",
    description: "Find decision makers and key contacts at target accounts. Build your pipeline with the right people, not just any people.",
    icon: Users,
    metric: "Smart Discovery",
  },
  {
    title: "Company Intelligence",
    description: "Understand company initiatives, competitive landscape, recent news, and financial insights to craft relevant messaging.",
    icon: FileText,
    metric: "360° View",
  },
];

export default function KeyAdvantagesSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    const section = document.getElementById('features-section');
    if (section) observer.observe(section);
    return () => { if (section) observer.unobserve(section); };
  }, []);

  return (
    <section id="features-section" className="bg-white dark:bg-slate-950 py-20 lg:py-28">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl">
            Everything You Need to Prepare
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-slate-400">
            Sabine automates the research and content creation so you can spend your time on what matters — the conversation.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className={`group p-6 rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-orange-200 dark:hover:border-orange-800/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ease-out ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + i * 80}ms` }}
              >
                <div className="inline-flex p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 mb-4 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 transition-colors">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 mb-3">
                  {feature.metric}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
